import React from 'react';
import { useLocation, useNavigate } from 'react-router';
import Authentication from './Authentication';
import { useState } from 'react';
import LoadingModal from '@components/LoadingModal/LoadingModal';
import SuccessModal from '@components/SuccessModal/SuccessModal';
import { useTranslation } from 'react-i18next';
import Snackbar from '@components/Snackbar/Snackbar';
import { ApiError } from '@lib/api';
import useManageTransactionRequest, { Destination } from '@hooks/useManageTransactionRequest';
import * as Sentry from "@sentry/react"
import { ModalWithIcon } from '@components/ModalWithIcon/ModalWithIcon';
import SlideCaptchaModal from '@components/SlideCaptcha/SlideCaptchaModal';
import { CaptchaRequiredResponse } from '../../types/captcha';

const AuthenticationWrapper = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isErrorModalVisible, setIsErrorModalVisible] = useState(false)
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [captchaData, setCaptchaData] = useState<CaptchaRequiredResponse | null>(null);
  const [currentOperation, setCurrentOperation] = useState<'redeem' | 'payment' | null>(null);
  const [captchaError, setCaptchaError] = useState('');

  // Get operation details from location state
  const {
    operationType,
    headerTitle,
    title,
    description,
    backRoute,
    successRoute,
    operationData,
    useBiometrics,
  } = location.state || {};

  const {
    requestBankRedemption,
    requestEWalletRedemption,
    requestPaymentBankRedemption,
    requestP2PTransfer,
    requestPrepaidPayment,
    requestPostpaidPayment
  } = useManageTransactionRequest()

  // Define API calls for different operations
  const handleRedeemOperation = async (captcha_key?: string, slide_x?: number, slide_y?: number) => {
    setIsLoading(true);
    try {
      const params = JSON.parse(operationData)

      let response
      let code
      const requestData = {
        destination: params.type,
        destination_uuid: params.redemptionTarget.id,
        ui_amount: params.amount,
        memo: params.memo,
        ...(captcha_key && slide_x !== undefined && slide_y !== undefined && {
          captcha_key,
          captcha_value: { slide_x, slide_y }
        })
      }

      if (params.type === 'bank') {
        code = 201
        response = await requestBankRedemption(requestData)
      } else {
        code = 203
        response = await requestEWalletRedemption(requestData)
      }

      // Check if response contains captcha requirement
      if (response && 'captcha_required' in response) {
        setCaptchaData(response);
        setCurrentOperation('redeem');
        setShowCaptcha(true);
        setCaptchaError('');
        setIsLoading(false);
        return;
      }

      // Success - navigate to completion
      navigate('/completed/processing-reviewed', {
        state: {
          uuid: response.activity.uuid,
          code
        }
      })
    } catch (error: any) {
      if (error instanceof ApiError && error.errorCode && error.errorCode === 2015) {
        setIsErrorModalVisible(true)
      } else {
        setErrorMessage(typeof error === 'string' ? error : String(error))
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleP2PTransferOperation = async () => {
    setIsLoading(true);
    try {
      const params = JSON.parse(operationData)
      const response = await requestP2PTransfer({
        destination_address: params.destination,
        ui_amount: params.amount,
        address_name: params.name,
        save_destination: params.save_address
      })
      navigate('/completed/processing', {
        state: {
          uuid: response.activity.uuid,
          code: 602
        }
      });
    } catch (error) {
      setErrorMessage(typeof error === 'string' ? error : String(error))
    } finally {
      setIsLoading(false);
    }
  };

  const handleRecoveryPhraseOperation = async () => {
    setIsLoading(true);
    try {
      // Handle API call for recovery phrase operation
      navigate('/show-recovery-phrase');
    } catch (error) {
      // Implement Sentry logging
      Sentry.captureException(error)
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddNewAccountOperation = async () => {
    setIsLoading(true);
    setSuccessMessage(t('authentication.successMessages.addedSuccessfully'));
    try {

      // Handle API call for add new account operation
      setSuccessOpen(true);
      setTimeout(() => { // Simulate API call; remove in production
        navigate(successRoute, { state: { operationData } });
      }, 2000);
    } catch (error) {
      // Implement Sentry logging
      Sentry.captureException(error)
      navigate(-1)
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveAccountOperation = async () => {
    setIsLoading(true);
    setSuccessMessage(t('authentication.successMessages.removedSuccessfully'));
    try {
      // Handle API call for remove account operation
      setSuccessOpen(true);
      setTimeout(() => { // Simulate API call; remove in production
        navigate(successRoute);
      }, 2000);
    } catch (error) {
      Sentry.captureException(error)
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditAccountOperation = async () => {
    setIsLoading(true);
    setSuccessMessage(t('authentication.successMessages.updatedSuccessfully'));
    try {
      // Handle API call for edit account operation
      setSuccessOpen(true);
      setTimeout(() => { // Simulate API call; remove in production
        navigate(successRoute);
      }, 2000);
    } catch (error) {
      Sentry.captureException(error)
    } finally {
      setIsLoading(false);
    }
  };

  const handleBankTransferPaymentOperation = async (captcha_key?: string, slide_x?: number, slide_y?: number) => {
    setIsLoading(true);
    try {
      const requestData = {
        destination: 'pay-bank' as Destination,
        destination_uuid: operationData.destination_uuid,
        ui_amount: operationData.ui_amount,
        memo: operationData.memo,
        ...(captcha_key && slide_x !== undefined && slide_y !== undefined && {
          captcha_key,
          captcha_value: { slide_x, slide_y }
        })
      }

      const response = await requestPaymentBankRedemption(requestData)

      // Check if response contains captcha requirement
      if (response && 'captcha_required' in response) {
        setCaptchaData(response);
        setCurrentOperation('payment');
        setShowCaptcha(true);
        setCaptchaError('');
        setIsLoading(false);
        return;
      }

      // Success - navigate to completion
      navigate(successRoute, {
        state: {
          activity_uuid: response.activity.uuid,
        }
      });
    } catch (error: any) {
      if (error instanceof ApiError) {
        if (error.errorCode && error.errorCode === 2015) {
          setIsErrorModalVisible(true)
          return
        }
        const translationKey = `errors.${error.errorCode}.description`
        const translatedMessage = t(translationKey)

        const errorMessage = translatedMessage !== translationKey
          ? translatedMessage
          : t('errors.genericServerDescription')

        setErrorMessage(errorMessage)
        return
      }
      setErrorMessage(t('errors.genericServerDescription'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleConvertSolanaTokenOperation = async () => {
    setIsLoading(true);
    try {
      navigate(successRoute);
    } catch (error) {
      Sentry.captureException(error)
    } finally {
      setIsLoading(false);
    }
  };

  const handleBiometricsOperation = async () => {
    setIsLoading(true);
    try {
      navigate(successRoute);
    } catch (error) {
      Sentry.captureException(error)
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrepaidPaymentOperation = async (captcha_key?: string, slide_x?: number, slide_y?: number) => {
    setIsLoading(true);
    try {
      const response = await requestPrepaidPayment(
        operationData.buyerSkuCode,
        operationData.customerNo,
        captcha_key && slide_x !== undefined && slide_y !== undefined ? {
          captcha_key,
          captcha_value: { slide_x, slide_y }
        } : undefined
      )

      // Check if response contains captcha requirement
      if (response && 'captcha_required' in response) {
        setCaptchaData(response);
        setCurrentOperation('payment');
        setShowCaptcha(true);
        setCaptchaError('');
        setIsLoading(false);
        return;
      }

      // Navigate to completed process page
      navigate('/completed/processing', {
        state: {
          txHash: response.txHash,
          escrowUuid: response.escrowUuid
        }
      });
    } catch (error: any) {
      if (error instanceof ApiError) {
        const translationKey = `errors.${error.errorCode}.description`
        const translatedMessage = t(translationKey)

        const errorMessage = translatedMessage !== translationKey
          ? translatedMessage
          : t('errors.genericServerDescription')

        setErrorMessage(errorMessage)
        return
      }
      setErrorMessage(t('errors.genericServerDescription'));
    } finally {
      setIsLoading(false);
    }
  };

  const handlePostpaidPaymentOperation = async (captcha_key?: string, slide_x?: number, slide_y?: number) => {
    setIsLoading(true);
    try {
      const response = await requestPostpaidPayment(
        operationData.buyerSkuCode,
        operationData.customerNo,
        operationData.inquiryQuote,
        captcha_key && slide_x !== undefined && slide_y !== undefined ? {
          captcha_key,
          captcha_value: { slide_x, slide_y }
        } : undefined
      )

      // Check if response contains captcha requirement
      if (response && 'captcha_required' in response) {
        setCaptchaData(response);
        setCurrentOperation('payment');
        setShowCaptcha(true);
        setCaptchaError('');
        setIsLoading(false);
        return;
      }

      // Navigate to completed process page
      navigate('/completed/processing', {
        state: {
          txHash: response.txHash,
          escrowUuid: response.escrowUuid
        }
      });
    } catch (error: any) {
      if (error instanceof ApiError) {
        const translationKey = `errors.${error.errorCode}.description`
        const translatedMessage = t(translationKey)

        const errorMessage = translatedMessage !== translationKey
          ? translatedMessage
          : t('errors.genericServerDescription')

        setErrorMessage(errorMessage)
        return
      }
      setErrorMessage(t('errors.genericServerDescription'));
    } finally {
      setIsLoading(false);
    }
  };

  // Select the appropriate operation handler based on operationType
  const getOperationHandler = () => {
    switch (operationType) {
      case 'redeem':
        return handleRedeemOperation;
      case 'p2p-transfer':
        return handleP2PTransferOperation;
      case 'bank-transfer-payment':
        return handleBankTransferPaymentOperation;
      case 'prepaid-payment':
        return handlePrepaidPaymentOperation;
      case 'postpaid-payment':
        return handlePostpaidPaymentOperation;
      case 'recovery-phrase':
        return handleRecoveryPhraseOperation;
      case 'add-new-account':
        return handleAddNewAccountOperation;
      case 'edit-account':
        return handleEditAccountOperation;
      case 'remove-account':
        return handleRemoveAccountOperation;
      case 'convert-solana-token':
        return handleConvertSolanaTokenOperation;
      case 'biometrics':
        return handleBiometricsOperation;
      default:
        return undefined;
    }
  };

  const handleCaptchaSuccess = async (captcha_key: string, slide_x: number, slide_y: number): Promise<boolean> => {
    setIsLoading(true);
    setCaptchaError('');

    try {
      let response;

      if (currentOperation === 'redeem') {
        const params = JSON.parse(operationData)
        const requestData = {
          destination: params.type,
          destination_uuid: params.redemptionTarget.id,
          ui_amount: params.amount,
          memo: params.memo,
          captcha_key,
          captcha_value: { slide_x, slide_y }
        }

        if (params.type === 'bank') {
          response = await requestBankRedemption(requestData)
        } else {
          response = await requestEWalletRedemption(requestData)
        }

        if (response && 'captcha_required' in response) {
          // Captcha failed, show new captcha
          setCaptchaData(response);
          setIsLoading(false);
          return false;
        } else {
          // Success
          const code = params.type === 'bank' ? 201 : 203;
          navigate('/completed/processing-reviewed', {
            state: {
              uuid: response.activity.uuid,
              code
            }
          })
          setShowCaptcha(false);
          setCaptchaData(null);
          setCurrentOperation(null);
          setIsLoading(false);
          return true;
        }
      } else if (currentOperation === 'payment') {
        // Check if it's a prepaid payment, postpaid payment or bank transfer payment
        if (operationData.buyerSkuCode && operationData.refId) {
          // Postpaid payment (phone bill)
          response = await requestPostpaidPayment(
            operationData.buyerSkuCode,
            operationData.customerNo,
            operationData.refId,
            {
              captcha_key,
              captcha_value: { slide_x, slide_y }
            }
          )

          if (response && 'captcha_required' in response) {
            // Captcha failed, show new captcha
            setCaptchaData(response);
            setIsLoading(false);
            return false;
          } else {
            // Success
            navigate('/completed/processing', {
              state: {
                txHash: response.txHash,
                escrowUuid: response.escrowUuid
              }
            });
            setShowCaptcha(false);
            setCaptchaData(null);
            setCurrentOperation(null);
            setIsLoading(false);
            return true;
          }
        } else if (operationData.buyerSkuCode) {
          // Prepaid payment (phone credit, phone data, PLN, etc.)
          response = await requestPrepaidPayment(
            operationData.buyerSkuCode,
            operationData.customerNo,
            {
              captcha_key,
              captcha_value: { slide_x, slide_y }
            }
          )

          if (response && 'captcha_required' in response) {
            // Captcha failed, show new captcha
            setCaptchaData(response);
            setIsLoading(false);
            return false;
          } else {
            // Success
            navigate('/completed/processing', {
              state: {
                txHash: response.txHash,
                escrowUuid: response.escrowUuid
              }
            });
            setShowCaptcha(false);
            setCaptchaData(null);
            setCurrentOperation(null);
            setIsLoading(false);
            return true;
          }
        } else {
          // Bank transfer payment
          const requestData = {
            destination: 'pay-bank' as Destination,
            destination_uuid: operationData.destination_uuid,
            ui_amount: operationData.ui_amount,
            memo: operationData.memo,
            captcha_key,
            captcha_value: { slide_x, slide_y }
          }

          response = await requestPaymentBankRedemption(requestData)

          if (response && 'captcha_required' in response) {
            // Captcha failed, show new captcha
            setCaptchaData(response);
            setIsLoading(false);
            return false;
          } else {
            // Success
            navigate(successRoute, {
              state: {
                activity_uuid: response.activity.uuid,
              }
            });
            setShowCaptcha(false);
            setCaptchaData(null);
            setCurrentOperation(null);
            setIsLoading(false);
            return true;
          }
        }
      }
    } catch (error: any) {
      // Dismiss captcha modal
      setShowCaptcha(false);
      setCaptchaData(null);
      setCurrentOperation(null);
      setCaptchaError('');
      setIsLoading(false);

      // Handle error code 2015 separately (show modal only, no snackbar)
      if (error instanceof ApiError && error.errorCode === 2015) {
        setIsErrorModalVisible(true);
        return false;
      }

      // Extract error message for snackbar
      let errorMsg = t('errors.genericServerDescription');
      if (error instanceof ApiError) {
        const translationKey = `errors.${error.errorCode}.description`;
        const translatedMessage = t(translationKey);
        errorMsg = translatedMessage !== translationKey
          ? translatedMessage
          : t('errors.genericServerDescription');
      }

      // Show error in snackbar
      setErrorMessage(errorMsg);

      return false;
    }

    setIsLoading(false);
    return false;
  };

  return (
    <React.Fragment>
      <Authentication
        headerTitle={headerTitle || t('authentication.defaultHeaderTitle')}
        title={title}
        description={description}
        backRoute={backRoute}
        onSuccess={getOperationHandler()}
        onFailure={() => {
          // Implement Sentry logging
        }}
        // If no operation type is specified, just go back to home
        successRoute={operationType ? undefined : '/home'}
        biometrics={!!useBiometrics}
      />
      <SlideCaptchaModal
        isOpen={showCaptcha}
        onClose={() => {
          setShowCaptcha(false);
          setCaptchaData(null);
          setCurrentOperation(null);
          setCaptchaError('');
          setIsLoading(false);
        }}
        onOverlayClose={() => {
          setShowCaptcha(false);
          setCaptchaData(null);
          setCurrentOperation(null);
          setCaptchaError('');
          setIsLoading(false);
          // Navigate back to previous page
          navigate(-1);
        }}
        onSuccess={handleCaptchaSuccess}
        captchaData={captchaData}
        error={captchaError}
      />
      <SuccessModal
        visible={successOpen}
        message={successMessage}
        onHide={() => setSuccessOpen(false)}
      />
      <Snackbar
        width='90%'
        duration={8000}
        message={errorMessage}
        isVisible={!!errorMessage}
        onClose={() => setErrorMessage('')}
      />
      <ModalWithIcon
        title={t('errors.genericRedemptionAccountDestinationError.title')}
        description={t('errors.genericRedemptionAccountDestinationError.description')}
        onPositive={() => {
          setIsErrorModalVisible(false)
          navigate(-1)
        }}
        positiveText={t('errors.verification.failed.positiveText')}
        visible={isErrorModalVisible}
        type="failed"
      />
      <LoadingModal visible={isLoading} />
    </React.Fragment>
  );
};

export default AuthenticationWrapper;